import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import asyncService from '../../../datahub/asyncService';
import selectUser from '../../store/selectors/appSelector';

import { getId, toIds } from './helpers';
import { patchPositionTemplateDetail } from './positionTemplateDetailReducer';
import { selectPositionTemplateDetail } from './positionTemplateDetailSelectors';
import { getPairedLocationIds } from './workersCompAdapter';

// Add / remove pairing endpoints do not update the legacy `locations` list, so the read can
// return pairings whose location it did not populate. Fetch those and merge them into
// `locations` so every pairing row has its name, address and state.
const useMissingPairingLocations = (positionTemplate) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { token, refreshToken } = useSelector(selectUser) || {};
  const requested = useRef({ templateId: undefined, ids: new Set() });
  const templateId = getId(positionTemplate);

  const requestedIds =
    requested.current.templateId === templateId ? requested.current.ids : new Set();
  const knownIds = new Set(toIds(positionTemplate?.locations));
  const missingKey = getPairedLocationIds(positionTemplate)
    .filter(
      (locationId) => locationId && !knownIds.has(locationId) && !requestedIds.has(locationId)
    )
    .join(',');

  useEffect(() => {
    if (!templateId || !missingKey) return;
    if (requested.current.templateId !== templateId) {
      requested.current = { templateId, ids: new Set() };
    }
    const { ids: pendingIds } = requested.current;
    const ids = missingKey.split(',');
    ids.forEach((locationId) => pendingIds.add(locationId));

    asyncService({
      httpMethod: 'POST',
      route: 'locations/read',
      data: {
        filters: [{ field: '_id', operation: 'idContainedIn', value: ids }],
        limit: ids.length,
        page: 0,
      },
      token,
      refreshToken,
      dispatch,
      onSuccess: (response) => {
        const current = selectPositionTemplateDetail(store.getState());
        if (getId(current) !== templateId) return;
        const currentIds = new Set(toIds(current.locations));
        const fetched = (response?.data?.documents || []).filter(
          (location) => !currentIds.has(getId(location))
        );
        if (fetched.length) {
          dispatch(
            patchPositionTemplateDetail({ locations: [...(current.locations || []), ...fetched] })
          );
        }
      },
      onError: () => {
        ids.forEach((locationId) => pendingIds.delete(locationId));
      },
    });
  }, [dispatch, missingKey, refreshToken, store, templateId, token]);
};

export default useMissingPairingLocations;
